import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import Linechart from 'components/molecules/Linechart';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { defaultApplyFilter } from 'components/pages/GrowthAnalysis';
import {
    graphItemContainerClass,
    subHeaderClass,
    yAxisLabelClass,
} from 'components/template/GrowthAnalysis/ExpandedView';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PVM_ANALYSIS_DOWNLOAD_DATA, PVM_ANALYSIS_COPY_SCREENSHOT } from 'utils/mixpanelEventNames';
import { colorConstants } from 'config/colorConstants';
import { PVMUrls } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchFeatureTimeSeries } from 'hooks/pvmAnalysis/useFetchFeatureTimeSeries';
import _ from 'lodash';
import {
    calculateFontSize,
    capitalizeFirstLetterOfEachWord,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getIntersectionOnApplyFilter,
} from 'utils/helperMethods';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';

type Props = {
    level2DrillDownItem: any;
    selectedAttribute: any;
    commonVariables: any;
    trendsChartFilters: any;
    exportTimePeriodHeader: any;
    applyFilterMutateDataLoading: boolean;
    decimalForNonPercentage: number;
    decimalForPercentage: number;
};

const chartHeight = calculateFontSize(window.innerWidth, 130);
const yAxisWidth = calculateFontSize(window.innerWidth, 80);

const PVMTrendChart2: FC<Props> = ({
    level2DrillDownItem,
    selectedAttribute,
    commonVariables: parentCommonVariables,
    trendsChartFilters,
    applyFilterMutateDataLoading,
    exportTimePeriodHeader,
    decimalForNonPercentage,
}) => {
    const { idForRestOfComponent, allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);

    const trendsChartRef = useRef<any>();
    const containerWidth = useContainerWidth({
        containerId: `pvm-trend-chart2`,
        buffer: yAxisWidth + 34 * 2,
    }); //subtracting graph width and padding on 2 sides

    const commonVariables = useMemo(() => {
        if (!applyFilterMutateDataLoading) {
            return { ...parentCommonVariables, filters: trendsChartFilters };
        }
        return {};
    }, [parentCommonVariables, trendsChartFilters, applyFilterMutateDataLoading]);

    const {
        data: lineChartResult,
        loading: timeSeriesFeatureDataLoading,
        error: timeSeriesFeatureDataError,
    } = useFetchFeatureTimeSeries({
        queryKey: ['pvm-timeseries-trend-chart2-feature-data', { ...commonVariables }],
        variables: { ...commonVariables },
        queryConfig: {
            enabled: !applyFilterMutateDataLoading && !!commonVariables?.filters,
        },
        timeSeriesObjKey: level2DrillDownItem?.category,
        url: PVMUrls.FeatureTimeSeriesUrl,
    });

    const header = `${capitalizeFirstLetterOfEachWord(parentCommonVariables?.pvm_feature_name?.replace(/_/g, ' '))} ${
        level2DrillDownItem?.category
    } Sales Trend Chart`;
    const combinedLoading = applyFilterMutateDataLoading || timeSeriesFeatureDataLoading;
    const combinedError = timeSeriesFeatureDataError;

    const currency = useGetCurrencyForTenant();
    let yAxisLabel: any = 'Sales ' + currency;

    // IF ERROR RETURN ERROR MESSAGE
    if (timeSeriesFeatureDataError && !combinedLoading) return <ErrorBox msg={String(combinedError)} />;
    // IF LOADING RETURN LOADER
    if (combinedLoading)
        return (
            <div
                className={graphItemContainerClass}
                style={{ height: 370, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Loader />
            </div>
        );

    return (
        <div ref={trendsChartRef} className={`${graphItemContainerClass} mt-4`} id="pvm-trend-chart2">
            <div className="flex items-center justify-between">
                <p className={`${subHeaderClass} mb-2`}>{header}</p>
                <div className="flex items-center justify-end gap-2">
                    <CopyToClipBoard
                        mixpanelEvent={() =>
                            trackEventMixpanel(PVM_ANALYSIS_COPY_SCREENSHOT, {
                                PVMSalesChart: `${header}`,
                            })
                        }
                        refOfComponent={trendsChartRef}
                    />
                    <DownloadExcel
                        customTimePeriodHeader={exportTimePeriodHeader}
                        title={header}
                        mixpanelEvent={() =>
                            trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                PVMSalesChart: `${header}`,
                            })
                        }
                        excelData={[
                            ...(lineChartResult?.data?.map((item: any) => [
                                item?.period,
                                item[level2DrillDownItem?.category],
                            ]) || []),
                        ]}
                        headers={['Period', level2DrillDownItem?.category]}
                    />
                </div>
            </div>

            <div className=" mb-1">
                <p className={`${yAxisLabelClass} relative mt-[-16px] top-5`}>{yAxisLabel}</p>
                <Legend data={[{ label: level2DrillDownItem.category, color: colorConstants.darkOrange }]} />
            </div>
            <div className="ml-1 flex">
                <Linechart
                    padding={[0, 0, 12, 0]}
                    // yAxisConfig={false}
                    xAxisConfig={false}
                    YGrid={false}
                    dataset={[]}
                    height={chartHeight}
                    loading={false}
                    chartContainer={`pvm-trends-chart2-y`}
                    fields={['period', level2DrillDownItem.category]}
                    minBound={lineChartResult?.minVal}
                    maxBound={lineChartResult?.maxVal}
                    width={yAxisWidth} //subtracting the padding
                    xAxisLabelFormatter={(val: any) => {
                        return val;
                    }}
                    yAxisLabelFormatter={(val: any) => {
                        return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                    }}
                    groupedChartColor={[colorConstants.darkOrange, colorConstants.lightBlue]}
                    colorKey={'color'}
                    disableIntervalLabel
                />
                <Linechart
                    // yAxisConfig={false}
                    dataset={lineChartResult?.data || []}
                    height={chartHeight}
                    loading={false}
                    chartContainer={`pvm-trends-chart2`}
                    fields={['period', level2DrillDownItem.category]}
                    minBound={lineChartResult?.minVal}
                    maxBound={lineChartResult?.maxVal}
                    width={containerWidth} //subtracting the padding
                    xAxisLabelFormatter={(val: any) => {
                        return val;
                    }}
                    groupedChartColor={[colorConstants.darkOrange, colorConstants.lightBlue]}
                    colorKey={'color'}
                    disableIntervalLabel
                    tooltipFormatter={(xAxis, val, from) => {
                        return {
                            name: from,
                            value: formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                        };
                    }}
                />
            </div>
            <p className={`text-center relative ${yAxisLabelClass} mt-4`}>Months </p>
        </div>
    );
};

export default PVMTrendChart2;
